import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, User, Crown, Eye, Trash2, CheckCircle, XCircle } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import api from '../utils/api';
import toast from 'react-hot-toast';
import Layout from './Layout';

interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  accountType: 'founder' | 'group_a' | 'owner';
  profilePictureUrl?: string;
  phoneNumber?: string;
  city?: string;
  title?: string;
  equityPercentage?: number;
  isTechnicalFounder?: boolean;
  workHistory?: string;
  education?: string;
  country?: string;
  postCode?: string;
  isEmailVerified: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ApiResponse {
  users: User[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

const UserManagement: React.FC = () => {
  const navigate = useNavigate();
  const { themeClasses } = useTheme();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    fetchUsers();
  }, [page]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get<ApiResponse>('/admin/users', {
        params: { page, limit: 20 }
      });
      
      const { users: newUsers, pagination } = response.data;
      
      if (page === 1) {
        setUsers(newUsers);
      } else {
        setUsers(prev => [...prev, ...newUsers]);
      }
      
      setHasMore(pagination.hasNext);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleUserAction = async (userId: number, action: 'verify' | 'delete', value?: boolean) => {
    try {
      if (action === 'verify') {
        await api.put(`/admin/users/${userId}/verify`, { isEmailVerified: value });
        toast.success('User verification status updated');
      } else if (action === 'delete') {
        await api.delete(`/admin/users/${userId}`);
        toast.success('User deleted successfully');
      }
      
      // Refresh the users list
      setPage(1);
      fetchUsers();
    } catch (error) {
      console.error(`Error ${action}ing user:`, error);
      toast.error(`Failed to ${action} user`);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getAccountTypeIcon = (accountType: string) => {
    switch (accountType) {
      case 'owner':
        return <Crown className="w-4 h-4 text-yellow-500" />;
      case 'founder':
        return <User className="w-4 h-4 text-blue-500" />;
      case 'group_a':
        return <Users className="w-4 h-4 text-green-500" />;
      default:
        return <User className="w-4 h-4 text-gray-500" />;
    }
  };

  const getAccountTypeColor = (accountType: string) => {
    switch (accountType) {
      case 'owner':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'founder':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'group_a':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <Layout title="User Management" showBackButton onBack={() => navigate('/')}>

      {/* Users List */}
      <div className="p-4 space-y-4">
        {users.map((user) => (
          <div key={user.id} className={`${themeClasses.card} rounded-lg`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {/* User Avatar */}
                <div className={`w-12 h-12 ${themeClasses.background === 'bg-gray-50' ? 'bg-gray-300' : 'bg-gray-600'} rounded-full flex items-center justify-center`}>
                  {user.profilePictureUrl ? (
                    <img
                      src={user.profilePictureUrl}
                      alt={`${user.firstName} ${user.lastName}`}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <User className={`w-6 h-6 ${themeClasses.textSecondary}`} />
                  )}
                </div>

                {/* User Info */}
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <h3 className={`text-lg font-semibold ${themeClasses.text}`}>
                      {user.firstName} {user.lastName}
                    </h3>
                    {getAccountTypeIcon(user.accountType)}
                  </div>
                  <p className={`${themeClasses.textSecondary} text-sm`}>{user.email}</p>
                  <div className="flex items-center space-x-2 mt-2">
                    <span className={`px-2 py-1 text-xs rounded-full border ${getAccountTypeColor(user.accountType)}`}>
                      {user.accountType === 'owner' ? 'Owner' : user.accountType === 'founder' ? 'Founder' : 'Investor'}
                    </span>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      user.isEmailVerified 
                        ? 'bg-green-100 text-green-800 border border-green-200' 
                        : 'bg-red-100 text-red-800 border border-red-200'
                    }`}>
                      {user.isEmailVerified ? 'Verified' : 'Unverified'}
                    </span>
                    <span className={`${themeClasses.textSecondary} text-xs`}>
                      Joined {formatDate(user.createdAt)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    setSelectedUser(user);
                    setShowUserDetails(true);
                  }}
                  className={`p-2 ${themeClasses.textSecondary} hover:${themeClasses.text} transition-colors rounded-lg`}
                  title="View Details"
                >
                  <Eye className="w-4 h-4" />
                </button>
                
                <button
                  onClick={() => handleUserAction(user.id, 'verify', !user.isEmailVerified)}
                  className={`p-2 rounded-lg transition-colors ${
                    user.isEmailVerified 
                      ? 'text-red-400 hover:text-red-300' 
                      : 'text-green-400 hover:text-green-300'
                  }`}
                  title={user.isEmailVerified ? 'Unverify User' : 'Verify User'}
                >
                  {user.isEmailVerified ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                </button>

                {user.accountType !== 'owner' && (
                  <button
                    onClick={() => {
                      if (confirm(`Are you sure you want to delete ${user.firstName} ${user.lastName}?`)) {
                        handleUserAction(user.id, 'delete');
                      }
                    }}
                    className="p-2 text-red-400 hover:text-red-300 transition-colors rounded-lg"
                    title="Delete User"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}

        {/* Load More Button */}
        {hasMore && (
          <div className="text-center">
            <button
              onClick={() => setPage(prev => prev + 1)}
              disabled={loading}
              className={`${themeClasses.buttonSecondary} px-6 py-2 rounded-lg font-medium transition-colors duration-200`}
            >
              {loading ? 'Loading...' : 'Load More'}
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && users.length === 0 && (
          <div className="text-center py-12">
            <Users className={`w-16 h-16 ${themeClasses.textSecondary} mx-auto mb-4`} />
            <p className={themeClasses.textSecondary}>No users found</p>
          </div>
        )}
      </div>

      {/* User Details Modal */}
      {showUserDetails && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${themeClasses.card} max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-lg`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-xl font-semibold ${themeClasses.text}`}>User Details</h2>
              <button
                onClick={() => setShowUserDetails(false)}
                className={`p-2 ${themeClasses.textSecondary} hover:${themeClasses.text} transition-colors rounded-lg`}
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Basic Info */}
              <div>
                <h3 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-1`}>Name</label>
                    <p className={themeClasses.text}>{selectedUser.firstName} {selectedUser.lastName}</p>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-1`}>Email</label>
                    <p className={themeClasses.text}>{selectedUser.email}</p>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-1`}>Account Type</label>
                    <div className="flex items-center space-x-2">
                      {getAccountTypeIcon(selectedUser.accountType)}
                      <span className={themeClasses.text}>{selectedUser.accountType.replace('_', ' ').toUpperCase()}</span>
                    </div>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-1`}>Status</label>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      selectedUser.isEmailVerified 
                        ? 'bg-green-100 text-green-800 border border-green-200' 
                        : 'bg-red-100 text-red-800 border border-red-200'
                    }`}>
                      {selectedUser.isEmailVerified ? 'Verified' : 'Unverified'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              {(selectedUser.phoneNumber || selectedUser.city || selectedUser.country) && (
                <div>
                  <h3 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>Contact Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedUser.phoneNumber && (
                      <div>
                        <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-1`}>Phone</label>
                        <p className={themeClasses.text}>{selectedUser.phoneNumber}</p>
                      </div>
                    )}
                    {selectedUser.city && (
                      <div>
                        <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-1`}>City</label>
                        <p className={themeClasses.text}>{selectedUser.city}</p>
                      </div>
                    )}
                    {selectedUser.country && (
                      <div>
                        <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-1`}>Country</label>
                        <p className={themeClasses.text}>{selectedUser.country}</p>
                      </div>
                    )}
                    {selectedUser.postCode && (
                      <div>
                        <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-1`}>Postal Code</label>
                        <p className={themeClasses.text}>{selectedUser.postCode}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Professional Info */}
              {(selectedUser.title || selectedUser.equityPercentage !== undefined || selectedUser.isTechnicalFounder) && (
                <div>
                  <h3 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>Professional Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedUser.title && (
                      <div>
                        <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-1`}>Title</label>
                        <p className={themeClasses.text}>{selectedUser.title}</p>
                      </div>
                    )}
                    {selectedUser.equityPercentage !== undefined && (
                      <div>
                        <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-1`}>Equity Percentage</label>
                        <p className={themeClasses.text}>{selectedUser.equityPercentage}%</p>
                      </div>
                    )}
                    {selectedUser.isTechnicalFounder !== undefined && (
                      <div>
                        <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-1`}>Technical Founder</label>
                        <p className={themeClasses.text}>{selectedUser.isTechnicalFounder ? 'Yes' : 'No'}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Timestamps */}
              <div>
                <h3 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>Account Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-1`}>Created</label>
                    <p className={themeClasses.text}>{formatDate(selectedUser.createdAt)}</p>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-1`}>Last Updated</label>
                    <p className={themeClasses.text}>{formatDate(selectedUser.updatedAt)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default UserManagement; 